/* eslint-disable jira/filename-case */

import { loadQuery, type LoadQueryOptions, type PreloadedQuery } from 'react-relay';
import {
	getRequest,
	type GraphQLTaggedNode,
	type PreloadableConcreteRequest,
	type OperationType,
	type VariablesOf,
} from 'relay-runtime';
import {
	// eslint-disable-next-line jira/restricted/@atlassian/react-resource-router
	createResource,
	type ResourceStoreContext,
	type RouterContext,
	type RouterDataContext,
	type RouteResource,
	useResource,
	type JiraSpaRoute,
} from '@atlassian/react-resource-router';
import { QueryPromisesMap } from '@atlassian/relay-query-promises';
import { startCapturingTraceIds } from '@atlassian/relay-traceid';
import getRelayEnvironment from '@jira/platform__graphql-relay-environment/src/index.tsx';
import { startCaptureGraphQlErrors } from '@jira/platform__graphql-relay-errors/src/index.tsx';
import { setMark, measureFunc, setMeasure } from '@jira/platform__performance/src/marks.tsx';
import { getRouteCapabilityHeaders } from '@jira/platform__routing-route-capability-header/src/getRouteCapabilityHeaders.tsx';

/**
 * @deprecated Use Relay EntryPoint instead https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095
 */
export const RELAY_RESOURCE_TYPE = 'RELAY_RESOURCE_TYPE';

type Config<TQuery extends OperationType> = {
	parameters: GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>;
	variables?: VariablesOf<TQuery>;
	options?: LoadQueryOptions;
};

type CreateRelayResourceConfig<TQuery extends OperationType> = {
	type: string;
	isBrowserOnly?: boolean;
	getQuery: (arg1: RouterContext | RouterDataContext, arg2: ResourceStoreContext) => Config<TQuery>;
	loadParameters?: () => Promise<GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>>;
	captureErrors?: boolean;
	captureTraceIds?: boolean;
	isPageShellCritical?: boolean;
	/**
	 * Optional predicate used to bypass the query at request-time. When provided
	 * and returning `true`, the resource short-circuits — `getKey` returns a
	 * stable sentinel and `getData` returns `null` without ever invoking
	 * `loadQuery` (so no network request is made). This is useful for opting
	 * out of fetching in specific tenant contexts (e.g. anonymous users).
	 *
	 * The predicate receives the same context arguments as `getQuery`, so it
	 * can read e.g. `tenantContext.isAnonymous`.
	 */
	skip?: (arg1: RouterContext | RouterDataContext, arg2: ResourceStoreContext) => boolean;
};

type GetDataConfig<TQuery extends OperationType> = {
	routerContext: RouterDataContext;
	customContext: ResourceStoreContext;
	type: string;
	loadParameters?: () => Promise<GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>>;
	getQuery: (arg1: RouterContext | RouterDataContext, arg2: ResourceStoreContext) => Config<TQuery>;
	captureErrors?: boolean;
	captureTraceIds?: boolean;
	skip?: (arg1: RouterContext | RouterDataContext, arg2: ResourceStoreContext) => boolean;
};

const addPrefetchCapabilityMetadata = (
	routerContext: RouterDataContext,
	options: LoadQueryOptions | undefined,
): LoadQueryOptions | undefined => {
	if (!routerContext.isPrefetch) {
		return options;
	}

	const headers = getRouteCapabilityHeaders(routerContext, {
		existingHeaders: options?.networkCacheConfig?.metadata?.headers,
	});

	if (headers === undefined) {
		return options;
	}

	return {
		...options,
		networkCacheConfig: {
			...options?.networkCacheConfig,
			metadata: {
				...options?.networkCacheConfig?.metadata,
				headers,
			},
		},
	};
};

const getData = <TQuery extends OperationType>({
	routerContext,
	customContext,
	type,
	getQuery,
	captureErrors,
	captureTraceIds,
	skip,
}: GetDataConfig<TQuery>) => {
	const measureName = `relay_resource_get_data:${type}`;
	const startMark = `relay_resource_get_data:${type}:start`;
	const endMark = `relay_resource_get_data:${type}:end`;

	setMark(startMark);

	// If the resource has opted out via the `skip` predicate, short-circuit
	// before invoking `getQuery` / `loadQuery` so no network request is made.
	if (skip?.(routerContext, customContext)) {
		setMark(endMark);
		setMeasure(measureName, startMark, endMark);
		return null;
	}

	const { parameters, variables, options } = getQuery(routerContext, customContext);

	// loadQuery returns not a Promise, but Observable.
	// Resource waits until query Observable is completed and returns a queryReference
	// later queryReference could be used by usePreloadedQuery
	const queryReference = measureFunc(`relay_resource_load_query:${type}`, () =>
		loadQuery(
			getRelayEnvironment(),
			parameters,
			variables || {},
			addPrefetchCapabilityMetadata(routerContext, options),
		),
	);

	if (__SERVER__ && queryReference.id != null) {
		const promise = QueryPromisesMap.get(queryReference.id);
		if (promise) {
			return promise.then(() => {
				setMark(endMark);
				setMeasure(measureName, startMark, endMark);

				/**
				 * We want to store HTTP codes from fetch response so that we can use it to report SLA correctly.
				 * (Unfortunately errors thrown by relay client validation doesn't include HTTP codes at the moment, so we have to store it ourselves)
				 * Starting to capture errors here ensures we capture errors for the early fetch of the query on initial page load,
				 * as well as subsequent fetches made by route transitions between spa-apps.
				 */
				if (captureErrors) {
					startCaptureGraphQlErrors(queryReference.fetchKey.toString());
				}

				if (captureTraceIds) {
					startCapturingTraceIds(queryReference.name);
				}

				// oxlint-disable-next-line typescript/consistent-type-assertions
				return queryReference as PreloadedQuery<TQuery>;
			});
		}
	}

	if (queryReference.id != null) {
		QueryPromisesMap.get(queryReference.id)?.then(() => {
			setMark(endMark);
			setMeasure(measureName, startMark, endMark);
		});
	}

	/**
	 * We want to store HTTP codes from fetch response so that we can use it to report SLA correctly.
	 * (Unfortunately errors thrown by relay client validation doesn't include HTTP codes at the moment, so we have to store it ourselves)
	 * Starting to capture errors here ensures we capture errors for the early fetch of the query on initial page load,
	 * as well as subsequent fetches made by route transitions between spa-apps.
	 */
	if (captureErrors) {
		startCaptureGraphQlErrors(queryReference.fetchKey.toString());
	}

	if (captureTraceIds) {
		startCapturingTraceIds(queryReference.name);
	}

	// oxlint-disable-next-line typescript/consistent-type-assertions
	return queryReference as PreloadedQuery<TQuery>;
};

const createRelayResourceImpl = <TQuery extends OperationType>({
	type,
	isBrowserOnly,
	getQuery,
	captureErrors = false,
	captureTraceIds = false,
	isPageShellCritical = false,
	skip,
}: CreateRelayResourceConfig<TQuery>): RouteResource<PreloadedQuery<TQuery> | null> =>
	createResource({
		// eslint-disable-next-line jira/router/require-static-resource-type -- MAGMA-6640: shared relay factory prefixes the caller-provided resource type.
		type: `${RELAY_RESOURCE_TYPE}_${type}`,
		getKey: (routerContext: RouterContext, customContext: ResourceStoreContext) => {
			// When skipped, use a stable sentinel key so all skipped invocations
			// dedupe to a single cache slot and never invoke `getQuery`.
			if (skip?.(routerContext, customContext)) {
				return '__skipped__';
			}

			const { parameters: preloadableRequest, variables } = getQuery(routerContext, customContext);
			// When providing an async query via `loadParameters` instead of getQuery's config, the query ID is not known upfront and so cannot be contributed to the resource key
			if (!preloadableRequest) {
				return JSON.stringify(variables || {});
			}

			let queryId;
			let params;

			// sources: https://github.com/facebook/relay/blob/4b78c7dd27b286f9ee8f5b993ccb160163956999/packages/react-relay/relay-hooks/loadQuery.js#L273
			// @ts-expect-error - TS2339 - Property 'kind' does not exist on type 'GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>'.
			if (preloadableRequest.kind === 'PreloadableConcreteRequest') {
				const preloadableConcreteRequest: PreloadableConcreteRequest<TQuery> =
					// oxlint-disable-next-line typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any
					preloadableRequest as any;
				({ params } = preloadableConcreteRequest);
				({ id: queryId } = params);
			} else {
				// oxlint-disable-next-line typescript/consistent-type-assertions, @typescript-eslint/no-explicit-any
				const graphQlTaggedNode: GraphQLTaggedNode = preloadableRequest as any;
				const request = getRequest(graphQlTaggedNode);
				params = request.params;
				queryId = 'cacheID' in params && params.cacheID != null ? params.cacheID : params.id;
			}

			return `${String(queryId)}${JSON.stringify(variables || {})}`;
		},
		getData: (routerContext: RouterDataContext, customContext: ResourceStoreContext) => {
			return getData({
				routerContext,
				customContext,
				type,
				getQuery,
				captureErrors,
				captureTraceIds,
				skip,
			});
		},
		maxAge: 0,
		isBrowserOnly,
		isPageShellCritical,
	});

// A public version of the internal `Config` type but with a mandatory `parameters`
type SyncConfig<TQuery extends OperationType> = {
	parameters: GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>;
	variables?: VariablesOf<TQuery>;
	options?: LoadQueryOptions;
};
// A public version of the internal `CreateRelayResourceConfig` type but with no `loadParameters`, since `SyncConfig` has a mandatory `parameters`
type CreateRelayResourceConfigSync<TQuery extends OperationType> = {
	type: string;
	isBrowserOnly?: boolean;
	getQuery: (
		arg1: RouterContext<JiraSpaRoute> | RouterDataContext<JiraSpaRoute>,
		arg2: ResourceStoreContext,
	) => SyncConfig<TQuery>;
	captureErrors?: boolean;
	captureTraceIds?: boolean;
	isPageShellCritical?: boolean;
	/**
	 * Optional predicate used to bypass the query at request-time. When it
	 * returns `true`, the resource short-circuits — `getKey` returns a stable
	 * sentinel and `getData` returns `null` without ever invoking `loadQuery`
	 * (so no network request is made). Useful for opting out of fetching for
	 * specific tenant contexts (e.g. anonymous users).
	 *
	 * The predicate receives the same context arguments as `getQuery`, so it
	 * can read e.g. `tenantContext.isAnonymous`.
	 *
	 * Note: consumers of `useRelayResource` already handle a `null`
	 * `queryReference`, so callers do not need to widen their own types.
	 */
	skip?: (
		arg1: RouterContext<JiraSpaRoute> | RouterDataContext<JiraSpaRoute>,
		arg2: ResourceStoreContext,
	) => boolean;
};
/**
 * @deprecated Use Relay EntryPoint instead https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095
 *
 * Creates a Relay powered route resource. For use when the graphql query is in the same chunk, otherwise use {@link createAsyncRelayResource}
 */
// oxlint-disable-next-line typescript/consistent-type-assertions
export const createRelayResource = createRelayResourceImpl as <TQuery extends OperationType>(
	param: CreateRelayResourceConfigSync<TQuery>,
) => RouteResource<PreloadedQuery<TQuery>>;

// A public version of the internal `Config` type but with no `parameters`, since `CreateRelayResourceConfigAsync` contains a `loadParameters`
type AsyncConfig<TQuery extends OperationType> = {
	variables?: VariablesOf<TQuery>;
	options?: LoadQueryOptions;
};
// A public version of the internal `CreateRelayResourceConfig` type but with a `loadParameters` type and the `getQuery` is not expected to return a `parameters` property.
type CreateRelayResourceConfigAsync<TQuery extends OperationType> = {
	type: string;
	isBrowserOnly?: boolean;
	loadParameters: () => Promise<GraphQLTaggedNode | PreloadableConcreteRequest<TQuery>>;
	getQuery: (
		arg1: RouterContext | RouterDataContext,
		arg2: ResourceStoreContext,
	) => AsyncConfig<TQuery>;
	captureErrors?: boolean;
	captureTraceIds?: boolean;
	isPageShellCritical?: boolean;
};

/**
 * @deprecated Use Relay EntryPoint instead https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095
 *
 * Creates a Relay powered route resource which loads a graphql query asyncronously before loading its data.
 */
// oxlint-disable-next-line typescript/consistent-type-assertions
export const createAsyncRelayResource = createRelayResourceImpl as <TQuery extends OperationType>(
	param: CreateRelayResourceConfigAsync<TQuery>,
) => RouteResource<PreloadedQuery<TQuery>>;

/**
 * @deprecated Use Relay EntryPoint instead https://hello.atlassian.net/wiki/spaces/UAF/pages/2754170095
 *
 * Access a relay resource
 */
export const useRelayResource = <TQuery extends OperationType>(
	resource: RouteResource<PreloadedQuery<TQuery>>,
	options?: { suspendWhenLoading?: boolean },
): {
	queryReference: PreloadedQuery<TQuery> | null;
} => {
	const { data: queryReference, loading, promise } = useResource<PreloadedQuery<TQuery>>(resource);

	if (options?.suspendWhenLoading && loading) {
		throw promise;
	}

	return {
		queryReference,
	};
};
